/* eslint-disable max-lines */
import {
  AIRecommendation as AIRecommendationType,
  Escalation,
  Situation,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { SOPWorkflowStateContext } from '@hakimo-ui/hakimo/util';
import { ComponentProps, useCallback, useEffect, useMemo, useRef } from 'react';
import { FloatingModalContainer } from '../sop-workflow/FloatingModalContainer';
import { ResolveAction } from '../sop-workflow/ResolveAction';
import { SectionStateProvider } from '../sop-workflow/SectionStateContext';
import type { SOPWorkflowContainer } from '../sop-workflow/SOPWorkflowContainer';
import { canPerformTalkdown, isAlarmGroupResolved } from '../sop-workflow/util';
import { useShouldHideAIRecommendation } from '../sop-workflow/utils';
import { buildTalkdownSteps } from './sop-card-utils';
import { useAlarmGroupStatic } from '../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { useAlarmGroupTracking } from '../unified-alarm-group/useAlarmGroupTracking';
import { SOPCardStateProvider } from './SOPCardStateContext';
import { SOPV2Panels } from './SOPV2Panels';
import { useTalkdownCameras } from './StepLiveTalkdown';
import { useSOPV2Resolve, useSOPV2Sop } from './useSOPV2Data';
import {
  defaultTabForStep,
  readPersistedStep,
  SOPV2Step,
  useSOPV2Machine,
  useSyncTabToStep,
} from './useSOPV2Machine';

export type SOPWorkflowV2ContainerProps = ComponentProps<
  typeof SOPWorkflowContainer
> & {
  escalation?: Escalation;
  cameraNames?: string[];
  aiOperatorRep?: AIRecommendationType;
  onResolveEscalation?: (
    comment?: string,
    reason?: string,
    category?: string
  ) => void;
};

export function SOPWorkflowV2Container(props: SOPWorkflowV2ContainerProps) {
  const isAlreadyEscalated =
    !!props.escalatedAlarmId &&
    props.escalatedAlarmId === props.locationAlarmId;

  const restoredStep = readPersistedStep(
    props.locationAlarmId,
    isAlreadyEscalated
  );
  const escalatedStep = isAlreadyEscalated
    ? SOPV2Step.ESCALATION_ACTIONS
    : SOPV2Step.OVERVIEW;
  const initialStep = restoredStep ?? escalatedStep;

  return (
    <SectionStateProvider>
      <SOPCardStateProvider initialTab={defaultTabForStep(initialStep)}>
        <SOPWorkflowV2Body {...props} isAlreadyEscalated={isAlreadyEscalated} />
      </SOPCardStateProvider>
    </SectionStateProvider>
  );
}

function SOPWorkflowV2Body(
  props: SOPWorkflowV2ContainerProps & { isAlreadyEscalated: boolean }
) {
  const {
    locationId,
    locationAlarmId,
    alarmCameras,
    onAlarmResolve,
    alarmGroupStatus,
    product,
    onEscalate,
    onQuickResolve,
    onCallFromSOP,
    locationTimezone,
    unauthorizedFaces,
    alarmStartEpochMs,
    alarmEndEpochMs,
    isAlreadyEscalated,
  } = props;

  const track = useAlarmGroupTracking();
  const { location } = useAlarmGroupStatic();

  const isScanContext = product === UnifiedMonitoringProduct.SCAN;
  const resolvedProduct = isScanContext
    ? UnifiedMonitoringProduct.SCAN
    : UnifiedMonitoringProduct.REMOTE_GUARDING;
  const isResolved = isAlarmGroupResolved(alarmGroupStatus);

  const [machineState, dispatch] = useSOPV2Machine(
    isAlreadyEscalated,
    locationAlarmId,
    isResolved
  );
  const state = isResolved
    ? { ...machineState, step: SOPV2Step.OVERVIEW }
    : machineState;

  useSyncTabToStep(state.step);

  const shouldHideAIRecommendation = useShouldHideAIRecommendation(
    locationAlarmId,
    resolvedProduct,
    unauthorizedFaces
  );

  const hadEscalation = useRef(false);
  useEffect(() => {
    if (isAlreadyEscalated) {
      hadEscalation.current = true;
      dispatch({ type: 'ALREADY_ESCALATED' });
    } else if (hadEscalation.current) {
      hadEscalation.current = false;
      dispatch({ type: 'ESCALATION_GONE' });
    }
  }, [isAlreadyEscalated, dispatch]);

  const { resolveMutation, onSubmitResolveAction, handleQuickResolve } =
    useSOPV2Resolve({
      isScanContext,
      locationAlarmId,
      onAlarmResolve,
      onQuickResolve,
      track,
      dispatch,
    });

  const onSituationSelect = useCallback(
    (situation: Situation) => {
      track('alarm_group_sop_situation_select', { situation: situation.label });
      dispatch({ type: 'SITUATION_SELECTED', situation });
    },
    [track, dispatch]
  );

  const talkdownCameras = useTalkdownCameras(alarmCameras);

  const logCallFromSOP = useCallback(
    (twilioCallSid: string, contactTag?: string) => {
      track('alarm_group_sop_call_contact', { tag: contactTag });
      onCallFromSOP?.(twilioCallSid, contactTag ?? '');
    },
    [track, onCallFromSOP]
  );

  const isOverview = !isResolved && state.step === SOPV2Step.OVERVIEW;

  const sop = useSOPV2Sop({
    locationId,
    locationTimezone,
    alarmStartEpochMs,
    alarmEndEpochMs,
    onSituationSelect: (isOverview && onSituationSelect) || undefined,
  });

  const hasTalkdown =
    !!sop.sopWorkflow && canPerformTalkdown(sop.sopWorkflow, talkdownCameras);

  const talkdownSteps = useMemo(
    () => (hasTalkdown ? buildTalkdownSteps(sop.sopWorkflow?.talkdowns) : []),
    [hasTalkdown, sop.sopWorkflow]
  );

  const onEscalationSubmitted = (
    comment?: string,
    situation?: string,
    subSituation?: string
  ) => {
    track('alarm_group_sop_pre_escalate_submit', { situation, subSituation });
    onEscalate(comment ?? '', situation, subSituation);
    dispatch({
      type: 'ESCALATION_SUBMITTED',
      hasTalkdown,
      deferred: isScanContext,
    });
  };

  const panelRef = useRef<HTMLDivElement>(null);
  const lastStep = useRef(state.step);
  useEffect(() => {
    if (lastStep.current === state.step) {
      return;
    }
    lastStep.current = state.step;
    panelRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [state.step]);

  if (state.step === SOPV2Step.RESOLVE) {
    return (
      <div className="px-3 pb-4 pt-2">
        <ResolveAction
          onClose={() => dispatch({ type: 'RESOLVE_CANCELLED' })}
          onSubmitResolveAction={onSubmitResolveAction}
          isLoading={resolveMutation.isLoading}
          actionWithSubactions={state.selectedQuickResolveAction}
        />
      </div>
    );
  }

  return (
    <SOPWorkflowStateContext.Provider value={{ logCallFromSOP }}>
      <div ref={panelRef} className="space-y-2.5 px-2.5 pb-5 pt-2">
        <SOPV2Panels
          {...props}
          state={state}
          dispatch={dispatch}
          sop={sop}
          location={location}
          isScanContext={isScanContext}
          resolvedProduct={resolvedProduct}
          hideAIDetails={shouldHideAIRecommendation}
          talkdownCameras={talkdownCameras}
          talkdownSteps={talkdownSteps}
          onQuickResolve={handleQuickResolve}
          onEscalated={onEscalationSubmitted}
          onAddEvent={(action) =>
            track('alarm_group_sop_add_event', { action })
          }
        />
      </div>
      <FloatingModalContainer sectionsContent={sop.sectionsContent} />
    </SOPWorkflowStateContext.Provider>
  );
}

export default SOPWorkflowV2Container;
